import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Button, Container, Typography, Paper, Grid, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';

// Hero section with a strawberry field background
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(8, 0),
  backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/strawberry-field.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  textAlign: 'center', // You'll need to add the strawberry-field.jpg image to your public folder
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  width: 60,
  height: 60,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  fontSize: '2rem',
}));

const LandingPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: '📊',
      title: 'Data Collection',
      description: 'Easily record and track all your growing conditions in one place.'
    },
    {
      icon: '📈',
      title: 'Visual Analytics',
      description: 'Visualize your data with intuitive charts and graphs to spot trends.'
    },
    {
      icon: '🔔',
      title: 'Smart Alerts',
      description: 'Get notified when conditions require your attention.'
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Track, Analyze, and Optimize Your Strawberry Crop Conditions
          </Typography>
          <Typography variant="h5" component="p" paragraph sx={{ mb: 4, maxWidth: '800px', margin: '0 auto' }}>
            Make data-driven decisions to improve your strawberry yields and quality with our comprehensive growth tracking platform.
          </Typography>
          <Box sx={{ '& > :not(style)': { m: 1 }, mt: 4 }}>
            {user ? (
              <Button 
                component={Link} 
                to="/dashboard" 
                variant="contained" 
                color="secondary" 
                size="large"
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  component={Link} 
                  to="/register" 
                  variant="contained" 
                  color="secondary" 
                  size="large"
                >
                  Get Started
                </Button>
                <Button 
                  component={Link} 
                  to="/login" 
                  variant="outlined" 
                  color="inherit" 
                  size="large"
                  sx={{ ml: 2 }}
                >
                  Sign In
                </Button>
              </>
            )}
          </Box>
        </Container>
      </HeroSection>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 6 }}>
          Why Choose Our Platform?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <FeatureCard elevation={3}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box bgcolor="primary.light" color="primary.contrastText" py={8}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Transform Your Strawberry Farming?
          </Typography>
          <Typography variant="h6" component="p" paragraph sx={{ mb: 4 }}>
            Join New Zealand's leading strawberry growers who are already making data-driven decisions.
          </Typography>
          <Button 
            component={Link} 
            to={user ? "/dashboard" : "/register"} 
            variant="contained" 
            color="secondary" 
            size="large"
          >
            {user ? 'Go to Dashboard' : 'Start Free Trial'}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
